import {ChangeEvent, FormEvent, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {
  Box,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Grow,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';

import {EmpionButton} from '../../../../../../../components/EmpionButton/EmpionButton';
import {empionGreen, empionOrange} from '../../../../../../../layouts/theme/EmpionColors';

interface PreQuestionnaireQuestionsProps {
  setFormData: (value: any) => void;
  startQuestionnaire: (e: any) => void;
  formData: any;
}

export const PreQuestionnaireQuestions = ({
  formData,
  setFormData,
  startQuestionnaire,
}: PreQuestionnaireQuestionsProps) => {
  const [validationError, setValidationError] = useState(false);
  const {t} = useTranslation();

  const isDarkMode = useTheme().palette.mode === 'dark';

  const handleRadioChange = (key: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [key]: event.target.value,
    });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (formData?.disciplinaryResponsibility && formData?.teamLeadershipResponsibility) {
      setValidationError(false);
      startQuestionnaire(e);
    } else {
      setValidationError(true);
    }
  };

  return (
    <Grow in style={{transformOrigin: '0 0 0'}} timeout={1200}>
      <Container>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          textAlign='center'
          sx={{
            backgroundColor: isDarkMode ? empionGreen[500] : empionOrange[3],
            padding: {xs: '2rem 1rem', sm: '2rem 1rem', md: '2rem 1rem', lg: '4rem 2rem'},
          }}
        >
          <Typography
            variant='h1'
            gutterBottom
            textTransform={'uppercase'}
            sx={{
              color: isDarkMode ? 'white' : empionGreen[900],
              mb: 5,
            }}
          >
            {t('companySurveys.public.questionnaireTypeQuestions.title')}
          </Typography>

          <form onSubmit={handleSubmit}>
            <FormControl component='fieldset' fullWidth>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormLabel component='legend'>
                    <Typography
                      variant='h3'
                      sx={{
                        fontSize: {xs: '20px', sm: '24px'},
                        wordWrap: 'break-word',
                        hyphens: 'auto',
                      }}
                    >
                      {t('companySurveys.public.questionnaireTypeQuestions.teamLeadershipResponsibility')}
                    </Typography>
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-label='Team Leadership Responsibility'
                    name='teamLeadershipResponsibility'
                    onChange={handleRadioChange('teamLeadershipResponsibility')}
                    sx={{
                      display: {xs: 'block', sm: 'flex'},
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <FormControlLabel value={true} control={<Radio />} label={t('shared.yes')} />
                    <FormControlLabel value={false} control={<Radio />} label={t('shared.no')} />
                  </RadioGroup>
                  <FormLabel component='legend'>
                    <Typography
                      variant='h3'
                      sx={{
                        fontSize: {xs: '20px', sm: '24px'},
                        wordWrap: 'break-word',
                        hyphens: 'auto',
                      }}
                    >
                      {t('companySurveys.public.questionnaireTypeQuestions.disciplinaryResponsibility')}
                    </Typography>
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-label='Disciplinary Responsibility'
                    name='disciplinaryResponsibility'
                    onChange={handleRadioChange('disciplinaryResponsibility')}
                    sx={{
                      display: {xs: 'block', sm: 'flex'},
                      justifyContent: 'center',
                    }}
                  >
                    <FormControlLabel value={true} control={<Radio />} label={t('shared.yes')} />
                    <FormControlLabel value={false} control={<Radio />} label={t('shared.no')} />
                  </RadioGroup>
                </Grid>
                <Grid item xs={12}>
                  {validationError && (
                    <FormHelperText
                      sx={{
                        color: 'red',
                        fontWeight: 'bold',
                        textAlign: 'center',
                      }}
                      error
                    >
                      {t('shared.formValidation.allFieldsRequired')}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <EmpionButton
                    type='submit'
                    size='large'
                    sx={{
                      marginTop: 2,
                      width: '100%',
                    }}
                  >
                    <Typography variant='h4' sx={{color: 'white'}}>
                      {t('shared.formSubmit.submit')}
                    </Typography>
                  </EmpionButton>
                </Grid>
              </Grid>
            </FormControl>
          </form>
        </Box>
      </Container>
    </Grow>
  );
};
